import React from 'react'
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLine,
  VictoryScatter,
} from 'victory'
import {VictoryStringOrNumberCallback} from 'victory-core'
import {DateTime} from 'luxon'
import {
  BarChartData,
  BarChartDataPoint,
  HealthDataGrade,
  HealthDataResolution,
} from '@common/models'
import {formatLocaleNumber, getGlucoseGrade} from '@common/utils'

import {TrendChartTooltip, tooltipEvents} from './TrendChartTooltip'
import {TrendChartContainer} from './TrendChart.styled'

import {getChartDomain, useInitialDimensions} from '~/utils'
import {theme} from '~/styles/theme'

type GlucoseTrendChartProps = {
  data: BarChartData
  fractionDigits: number
  resolution: HealthDataResolution
  startDate: DateTime
  unit: string | undefined
}

// Define fill colors based on health data grade
const fillColors = {
  [HealthDataGrade.GOOD]: theme.colors.indicator.body.green,
  [HealthDataGrade.AVERAGE]: theme.colors.indicator.body.orange,
  [HealthDataGrade.BAD]: theme.colors.indicator.body.red,
}

// Define stroke colors based on health data grade
const strokeColors = {
  [HealthDataGrade.GOOD]: theme.colors.indicator.border.green,
  [HealthDataGrade.AVERAGE]: theme.colors.indicator.border.orange,
  [HealthDataGrade.BAD]: theme.colors.indicator.border.red,
}

// Get bar color based on glucose grade
const getFillColor: VictoryStringOrNumberCallback = ({datum}) => {
  const minValue = datum.y0 // Min value
  const maxValue = datum.y // Max value
  const avgValue = (datum as BarChartDataPoint & {avgY: number}).avgY

  const grade = getGlucoseGrade(minValue, maxValue, avgValue)

  return grade && grade in HealthDataGrade
    ? fillColors[grade]
    : theme.colors.indicator.body.blue
}

// Get point stroke color based on glucose grade
const getStrokeColor: VictoryStringOrNumberCallback = ({datum}) => {
  const minValue = datum.y0 // Min value
  const maxValue = datum.y // Max value
  const avgValue = (datum as BarChartDataPoint & {avgY: number}).avgY

  const grade = getGlucoseGrade(minValue, maxValue, avgValue)

  return grade && grade in HealthDataGrade
    ? strokeColors[grade]
    : theme.colors.indicator.border.blue
}

export const GlucoseTrendChart: React.FC<GlucoseTrendChartProps> = ({
  data,
  fractionDigits,
  resolution,
  startDate,
  unit,
}) => {
  const {dimensions, ref} = useInitialDimensions()

  return (
    <TrendChartContainer ref={ref} fullWidth>
      <VictoryChart
        domainPadding={{x: 8, y: 10}}
        padding={{left: 10, right: 10, bottom: 0, top: 0}}
        height={dimensions?.height}
        width={dimensions?.width}
        scale={{x: 'time', y: 'linear'}}
        domain={getChartDomain(resolution, startDate)}>
        {/* Bars representing min-max range */}
        <VictoryBar
          barWidth={16}
          data={data}
          events={tooltipEvents}
          labels={({datum}) => datum.date}
          labelComponent={
            <TrendChartTooltip
              unit={unit}
              dy={-8}
              resolution={resolution}
              formatValue={(datum: BarChartDataPoint) =>
                `Max: ${formatLocaleNumber(
                  datum.y,
                  fractionDigits,
                )}\nMin: ${formatLocaleNumber(
                  datum.y0,
                  fractionDigits,
                )}\nAvg: ${formatLocaleNumber(
                  (datum as BarChartDataPoint & {avgY: number}).avgY,
                  fractionDigits,
                )}`
              }
            />
          }
          x="date"
          cornerRadius={{top: 4, bottom: 4}}
          style={{
            data: {fill: getFillColor},
          }}
        />
        {/* Line connecting average values */}
        <VictoryLine
          data={data as Array<BarChartDataPoint & {avgY: number}>}
          x="date"
          y="avgY"
          style={{
            data: {
              stroke: theme.colors.indicator.body.blue,
              strokeWidth: 1.5,
            },
          }}
        />
        {/* Points representing average values */}
        <VictoryScatter
          size={4}
          data={data as Array<BarChartDataPoint & {avgY: number}>}
          x="date"
          y="avgY"
          style={{
            data: {
              fill: theme.colors.white,
              stroke: getStrokeColor,
              strokeWidth: 2,
            },
          }}
        />
        <VictoryAxis
          dependentAxis
          style={{
            axis: {stroke: 'transparent'},
            ticks: {stroke: 'transparent'},
            tickLabels: {fill: 'transparent'},
          }}
        />
      </VictoryChart>
    </TrendChartContainer>
  )
}
